import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Target, Eye, Radio, BarChart3, Wrench, ExternalLink } from "lucide-react";
import { motion } from "framer-motion";

const platforms = [
  {
    name: "UAVSimNet",
    tagline: "Simulation & Decision Support",
    description: "Simulation and decision support for teams that need quantified evidence before live testing, procurement or deployment. Model inputs, campaign execution, outputs, provenance and limitations—all traceable.",
    icon: Target,
    href: "https://c-uas.co.uk",
    external: true,
    status: "Production",
  },
  {
    name: "AI-AV",
    tagline: "Vision-Based Autonomous Guidance",
    description: "Vision-based target-oriented adaptive control for autonomous platforms. Designed to operate without GPS for the relevant guidance function, subject to platform and sensor configuration.",
    icon: Eye,
    href: "/platforms/ai-av",
    status: "Production",
  },
  {
    name: "Helais",
    tagline: "Mission Orchestration Platform",
    description: "Mission orchestration and aerial-operations platform. Lead with mission control, asset coordination, dispatch workflow and continuity. RLK airframes are optional programme concepts or supported configurations.",
    icon: Radio,
    href: "/platforms/helais",
    status: "Production",
  },
  {
    name: "UrbanAnalytix",
    tagline: "Operational Intelligence",
    description: "Operational intelligence from existing infrastructure. Convert observations into defined metrics, alerts or workflow outputs with clear data governance and measurable outcomes.",
    icon: BarChart3,
    href: "https://urban-analytix.co.uk",
    external: true,
    status: "Production",
  },
];

export default function PlatformsPage() {
  return (
    <div className="relative">
      {/* Hero */}
      <section className="pt-32 pb-16 relative">
        <div className="absolute inset-0 grid-pattern opacity-50" />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-caption mb-4">Platforms & Technology</p>
            <h1 className="text-display text-foreground mb-6">
              Products & Reusable Technology
            </h1>
            <p className="text-subtitle max-w-2xl">
              RLK platforms can be used independently or integrated with qualified customer and partner systems through defined interfaces. No requirement to adopt a bundle.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Platforms Grid */}
      <section className="section-padding-lg">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid md:grid-cols-2 gap-6">
            {platforms.map((platform, index) => {
              const CardContent = (
                <motion.article
                  className="card-pillar h-full p-8"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="h-12 w-12 rounded border border-border bg-muted/50 flex items-center justify-center">
                      <platform.icon className="h-6 w-6 text-muted-foreground" />
                    </div>
                    <div className="flex items-center gap-2">
                      {platform.external && <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />}
                      <span className="text-xs text-muted-foreground bg-muted/50 px-2 py-1 rounded">
                        {platform.status}
                      </span>
                    </div>
                  </div>
                  <h2 className="text-title text-foreground mb-1 group-hover:text-foreground/90 transition-colors">
                    {platform.name}
                  </h2>
                  <p className="text-sm text-muted-foreground mb-4">{platform.tagline}</p>
                  <p className="text-body mb-6">{platform.description}</p>
                  <span className="inline-flex items-center text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                    {platform.external ? "Visit website" : "Learn more"}
                    {platform.external ? (
                      <ExternalLink className="h-4 w-4 ml-2" />
                    ) : (
                      <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    )}
                  </span>
                </motion.article>
              );

              return platform.external ? (
                <a key={index} href={platform.href} target="_blank" rel="noopener noreferrer" className="group">
                  {CardContent}
                </a>
              ) : (
                <Link key={index} to={platform.href} className="group">
                  {CardContent}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Advanced Development */}
      <section className="section-padding-lg bg-card/30 edge-glow">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mx-auto text-center">
            <div className="h-12 w-12 rounded border border-border bg-muted/50 flex items-center justify-center mx-auto mb-6">
              <Wrench className="h-6 w-6 text-muted-foreground" />
            </div>
            <h2 className="text-headline text-foreground mb-4">
              Advanced Development & Design Assets
            </h2>
            <p className="text-body mb-8">
              TALON, the fixed-wing C-UAS interceptor, optical-RF fusion and other development programmes. Each entry shows maturity, available evidence, commercial route and next development step.
            </p>
            <p className="text-sm text-muted-foreground mb-8 italic">
              Design assets and development programmes are not production-ready platforms. This section prevents concepts and licensable assets from being mistaken for deployed products.
            </p>
            <Link to="/platforms/advanced-development" className="btn-secondary">
              View Development Programmes
              <ArrowRight className="h-4 w-4 ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding-lg">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-headline text-foreground mb-6">
              Discuss an Integration
            </h2>
            <p className="text-body mb-8">
              Tell us about your platform, interface requirements, and the RLK capability you need. We will scope an integration approach.
            </p>
            <Link to="/contact?type=integration" className="btn-primary">
              Request Technical Briefing
              <ArrowRight className="h-4 w-4 ml-2" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
